import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { BlockVisualization, GranulationTypeId } from 'webapp/enums/enums';
import { BillingFeature, SortFieldType, Theme } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockSettingsAiSummaryComponent from 'webapp/components/block/settings/ai-summary';
import BlockSettingsConversionComponent from 'webapp/components/block/settings/conversion';
import BlockSettingsFontSizeComponent from 'webapp/components/block/settings/font-size';
import BlockSettingsHideTimeLeftComponent from 'webapp/components/block/settings/hide-time-left';
import BlockSettingsImageComponent from 'webapp/components/block/settings/image';
import BlockSettingsItemsInLineComponent from 'webapp/components/block/settings/items-in-line';
import BlockSettingsLabelHeaderComponent from 'webapp/components/block/settings/label-header';
import BlockSettingsLegendPositionComponent from 'webapp/components/block/settings/legend-position';
import BlockSettingsMediaCreativesComponent from 'webapp/components/block/settings/media-creatives';
import BlockSettingsMetricAggregationComponent from 'webapp/components/block/settings/metric/aggregation';
import BlockSettingsMetricShownLabelsComponent from 'webapp/components/block/settings/metric/shown-labels';
import BlockSettingsMetricSortDataSelectComponent from 'webapp/components/block/settings/metric/sort-data-select';
import BlockSettingsMetricStackedComponent from 'webapp/components/block/settings/metric/stacked';
import BlockSettingsMetricsComponent from 'webapp/components/block/settings/metrics';
import BlockSettingsNumberRowsComponent from 'webapp/components/block/settings/number-rows';
import BlockSettingsOnlyMainMetricDimensionsComponent from 'webapp/components/block/settings/only-main-metric-dimensions';
import BlockSettingsQuickCreateComponent from 'webapp/components/block/settings/quick-create';
import BlockSettingsRecordLimitComponent from 'webapp/components/block/settings/record-limit';
import BlockSettingsShowNumberComponent from 'webapp/components/block/settings/show-number';
import BlockSettingsShowOtherComponent from 'webapp/components/block/settings/show-other';
import BlockSettingsShowTotalsComponent from 'webapp/components/block/settings/show-totals';
import BlockSettingsSmallChartComponent from 'webapp/components/block/settings/small-chart';
import BlockSettingsSortFieldComponent from 'webapp/components/block/settings/sort-field';
import BlockSettingsSortOrderComponent from 'webapp/components/block/settings/sort-order';
import BlockSettingsSplineComponent from 'webapp/components/block/settings/spline';
import BlockSettingsTableColumnInvertComponent from 'webapp/components/block/settings/table-column-invert';
import BlockSettingsTableColumnTypeComponent from 'webapp/components/block/settings/table-column-type';
import BlockSettingsTextComponent from 'webapp/components/block/settings/text';
import BlockSettingsTrafficLightsComponent from 'webapp/components/block/settings/traffic-lights';
import BlockSettingsVisualizationSwitchConfirmComponent from 'webapp/components/block/settings/visualization-switch-confirm';
import BlockSettingsVisualizationsDropdownComponent from 'webapp/components/block/settings/visualizations-dropdown';
import BlockSettingsYAxisComponent from 'webapp/components/block/settings/y-axis';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DesignerPanelSectionComponent from 'webapp/components/designer/panel-section';
import FormCumulativeSwitchComponent from 'webapp/components/form/cumulative-switch';
import FormGranulationDropdownComponent from 'webapp/components/form/granulation/dropdown';
import FormInputComponent from 'webapp/components/form/input';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';

import type Block from 'webapp/models-custom/block';
import type { ChangeVisualizationWithPossibleConfirmFnReturnType } from 'webapp/models-custom/block-setup';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DateRange from 'webapp/models-custom/date-range';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';

import type { DesignerTourSettings } from 'webapp/services/flow/tour/designer';

interface Args {
	model: Block;
	isPanelOpen: boolean;
}

interface BlockSettingsComponentInterface<T> {
	Args: T;
	Blocks: {
		// this is needed for yield
		additionalSettings: [];
	};
}

enum SettingSection {
	VISUALIZATION = 1,
	METRICS = 2,
	OTHER = 3,
}

export default class BlockSettingsComponent extends Component<
	BlockSettingsComponentInterface<Args>
> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('tour') declare tourService: Service.Tour;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	@tracked isSectionVisualizationOpen = true;
	@tracked isSectionMetricsOpen = true;
	@tracked isSectionOtherOpen = false;

	get blockSetup(): BlockSetup {
		return this.args.model.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get showImageSettings(): boolean {
		return this.visualization.isImage;
	}

	get showTextSettings(): boolean {
		return this.visualization.isText;
	}

	get hasAnyDataSelects(): boolean {
		return this.blockSetup.dataSelects.length > 0;
	}

	get showBlockTitle() {
		return !this.args.model.isFromReportStoryText;
	}

	get showVisualizationSwitch() {
		return !this.args.model.isFromReportStoryText;
	}

	get showSortOrder() {
		return this.args.model.sortField !== SortFieldType.CUSTOM;
	}

	get showQuickCreateFromBoard() {
		if (this.visualization.showQuickCreateFromBoard) {
			// other blocks on the board must have some metrics selected
			const hasMetrics = (this.args.model.boardObj?.blockObjs || []).some((b) => {
				if (b.id !== this.args.model.id) {
					return !b.isEmpty;
				}
				return false;
			});
			return hasMetrics;
		} else {
			return false;
		}
	}

	get darkTheme(): Theme {
		return Theme.DARK;
	}

	get showSmallChartInAdditionalSettings() {
		return this.visualization.allowSmallChart && this.blockSetup.dataSelectsShown.length > 1;
	}

	get showTotalsChoice() {
		return (
			this.visualization.allowShowTotalsChoice &&
			this.blockSetup.dataSelectsShown.some((ds) => ds.isStacked)
		);
	}

	private toggleSection(sectionId: SettingSection): void {
		switch (sectionId) {
			case SettingSection.VISUALIZATION:
				this.isSectionVisualizationOpen = !this.isSectionVisualizationOpen;
				break;
			case SettingSection.METRICS:
				this.isSectionMetricsOpen = !this.isSectionMetricsOpen;
				break;
			case SettingSection.OTHER:
				this.isSectionOtherOpen = !this.isSectionOtherOpen;
				break;
		}
	}

	@action
	doNothing(): void {
		// nothing
	}

	@action
	toggleSectionVisualization(): void {
		this.toggleSection(SettingSection.VISUALIZATION);
	}

	@action
	toggleSectionMetrics(): void {
		this.toggleSection(SettingSection.METRICS);
	}

	@action
	toggleSectionOther(): void {
		this.toggleSection(SettingSection.OTHER);
	}

	@action
	onMetricOpen(): void {
		// programmatically with AppEvent.BLOCK_CONTENT_CLICKED (from block content)
		if (!this.isSectionMetricsOpen) {
			this.toggleSectionMetrics();
		}
	}

	@action
	updateTitle(): void {
		const model = this.args.model;
		if (model) {
			// can be triggered while componet is being destroyed bcos of focusOut
			model.name = model.name.trim();
			void model.saveAndCatch();
			this.analyticsService.event(TrackingEvent.DESIGNER_BLOCK_NAME, {
				origin: `datablock_settings_${model.trackingOrigin}`,
			});
		}
	}

	@action
	handleChangeDateRange(dateRange: DateRange): void {
		const changeDateRange = () => {
			const blockSetup = this.blockSetup;
			blockSetup.changeDateRange(dateRange);
			this.args.model.setAiSummaryEditInfoToNull();

			blockSetup.trackMetricDataEvent(TrackingEvent.DATE_RANGE_CHANGED);
			this.flowTourDesignerService.endTour(TourId.DESIGNER__CHANGE_DATE_RANGE);
		};

		if (this.args.model.isAiSummary && !this.args.model.isOnTemplate) {
			(this.visualization as VisualizationAiSummary).showRegenerationWarning(
				changeDateRange,
				() => {
					// no action on reject
				},
			);
		} else {
			changeDateRange();
		}
	}

	@action
	changeGranulation(value: string | null): void {
		const granulationId = value as GranulationTypeId;
		const blockSetup = this.blockSetup;
		blockSetup.changeGranulation(granulationId);
		blockSetup.trackMetricDataEvent(TrackingEvent.GRANULATION_CHANGED);
	}

	@action
	changeCumulative(cumulative: boolean): void {
		this.blockSetup.changeCumulative(cumulative);
		this.blockSetup.trackBlockPropertySettingsChangeEvent({ cumulative });
	}

	@action
	addFirstDataSelect(): void {
		const dateRange = this.mmBlocksService.getValidCommonDateRange(
			this.args.model.boardObj?.commonBlockDateRange,
			this.blockSetup.visualizationType,
		);

		const dataSelect = this.blockSetup.createPrimaryMainDataSelect({
			dateRange: dateRange?.formula,
		});
		this.blockSetup.saveDataSelect(dataSelect);
		void this.blockSetup.afterSaves().then(() => {
			if (!dataSelect.isNew) {
				this.eventManagerService.trigger(AppEvent.DESIGNER_UNIBLOCK_DATA_SELECT_ADDED, {
					dataSelect,
				});
			}
		});
		this.isSectionMetricsOpen = true;
	}

	// ----- Visualization change "from outside" (e.g. vis popup) -----

	@action
	onVisualizationChange(): void {
		if (this.blockSetup.visualizationType !== this.blockSetup.visualization.visualizationType) {
			this.blockSetup.recreateVisualization();
			this.rejectVisualizationChange();
		}
	}

	// ----- Visualization change confirm -----

	@tracked onVisualizationChangeConfirm: ChangeVisualizationWithPossibleConfirmFnReturnType =
		null;
	@tracked changeVisualizationConfirmRecordLimit = 1;

	get showChangeVisualizationConfirm() {
		return !!this.onVisualizationChangeConfirm;
	}

	@action
	changeVisualization(toVisualizationType: BlockVisualization): boolean {
		if (this.args.model.isFromReport) {
			// on reports, if visualization is not available, instead of actually switching
			// and immediately covering it in (report-specific) an limitation overlay,
			// we want to show limitation popup
			const limitation = this.args.model.getLimitationForVisualization(toVisualizationType);
			if (limitation) {
				const actionPassesLimitations = this.billingService.checkIfCan(limitation);

				if (!actionPassesLimitations) {
					const trackingOrigin =
						limitation === BillingFeature.GENERATIVE_AI
							? 'generative_ai_block'
							: undefined;

					this.billingService.handleLimitations(limitation, trackingOrigin);
					return true;
				}
			}
		}

		this.analyticsService.event(TrackingEvent.BLOCK_VISUALIZATION_CHANGED, {
			origin: `datablock_settings_${this.args.model.trackingOrigin}`,
			visualization:
				this.mmBlocksService.getVisualizationNameFromVisualizationType(toVisualizationType),
		});
		this.onVisualizationChangeConfirm =
			this.blockSetup.changeVisualizationWithPossibleConfirm(toVisualizationType);
		this.changeVisualizationConfirmRecordLimit =
			this.blockSetup.getMaxPrimaryMainDataSelectsAllowed(toVisualizationType);
		return this.showChangeVisualizationConfirm;
	}

	@action
	confirmVisualizationChange() {
		const onConfirm = this.onVisualizationChangeConfirm;
		if (onConfirm) {
			onConfirm();
		}
		this.onVisualizationChangeConfirm = null;
	}

	@action
	rejectVisualizationChange() {
		this.onVisualizationChangeConfirm = null;
	}

	@action
	removeTitle() {
		this.args.model.name = '';
		void this.args.model.saveAndCatch();
	}

	// ----- Tour -----

	node: HTMLElement | null = null;

	availableTourSettings: DesignerTourSettings = {
		id: TourId.DESIGNER__CHANGE_DATE_RANGE,
		checkStartConditions: this.checkDateRangeTourStartConditions.bind(this),
	};

	private checkDateRangeTourStartConditions() {
		return isAlive(this) && this.args.isPanelOpen && this.isSectionVisualizationOpen;
	}

	@action
	setupView(node: HTMLElement) {
		const tourSettings = this.availableTourSettings;
		this.flowTourDesignerService.bindTourCheck(tourSettings.id, this, () => {
			later(() => {
				if (tourSettings.checkStartConditions()) {
					this.flowTourDesignerService.startTour(tourSettings.id);
				}
			}, 500); // delay in case user just dropped new block
		});

		this.node = node;
	}

	@action
	teardownView() {
		this.flowTourDesignerService.unbindTourCheck(this.availableTourSettings.id, this);
	}

	@action
	checkToEndTour() {
		const tourSettings = this.availableTourSettings;
		if (
			(!this.args.isPanelOpen || !this.isSectionVisualizationOpen) &&
			this.flowTourDesignerService.activeTour?.id === tourSettings.id
		) {
			this.flowTourDesignerService.endTour(tourSettings.id);
		}
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{didUpdate this.checkToEndTour @isPanelOpen this.isSectionVisualizationOpen}}
		>
			{{#if this.showImageSettings}}

				<DesignerPanelSectionComponent
					@title="Visualization"
					@index={{1}}
					@isOpen={{true}}
					@toggle={{this.doNothing}}
				>
					<BlockSettingsImageComponent @blockM={{@model}} />
				</DesignerPanelSectionComponent>

			{{else if this.showTextSettings}}

				<DesignerPanelSectionComponent
					@title="Visualization"
					@index={{1}}
					@isOpen={{true}}
					@toggle={{this.doNothing}}
				>
					<BlockSettingsTextComponent @blockM={{@model}} />
				</DesignerPanelSectionComponent>

			{{else}}

				<DesignerPanelSectionComponent
					@title="Visualization"
					@index={{1}}
					@isOpen={{this.isSectionVisualizationOpen}}
					@toggle={{this.toggleSectionVisualization}}
				>

					{{#if this.showVisualizationSwitch}}
						<div
							class="row row--lr"
							{{didUpdate this.onVisualizationChange @model.visualization}}
						>
							<label>Type</label>
							<div class="right">
								<BlockSettingsVisualizationsDropdownComponent
									@blockM={{@model}}
									@onChange={{this.changeVisualization}}
									@isReports={{@model.isFromReport}}
									@theme={{this.darkTheme}}
								/>
							</div>
						</div>

						{{#if this.showChangeVisualizationConfirm}}
							<BlockSettingsVisualizationSwitchConfirmComponent
								@max={{this.changeVisualizationConfirmRecordLimit}}
								@onConfirm={{this.confirmVisualizationChange}}
								@onReject={{this.rejectVisualizationChange}}
							/>
						{{/if}}
					{{/if}}

					{{#if this.hasAnyDataSelects}}

						{{#if this.showBlockTitle}}
							<div class="row row--lr">
								<label>Title</label>
								<div class="right block-title">
									<FormInputComponent
										name="name"
										maxlength="50"
										placeholder="Add title"
										@value={{@model.name}}
										@focusOut={{this.updateTitle}}
										@loseFocusOnEnter={{true}}
										@hideFloatLabel={{true}}
										@theme="dark"
									/>
									{{#if @model.name}}
										<a
											role="button"
											class="title-remove-button title-remove-button--{{this.darkTheme}}"
											{{on "click" this.removeTitle}}
										>
											Remove
										</a>
									{{/if}}
								</div>
							</div>
						{{/if}}

						<div class="row row--lr">
							<label>Date range</label>
							<div class="right date-range">
								<FormRangeDropdownComponent
									@metricLimitations={{this.blockSetup.limitations}}
									@onChange={{this.handleChangeDateRange}}
									@theme="dark"
								/>
							</div>
						</div>

						{{#if this.visualization.showGranulation}}
							<div class="row row--granulation row--lr">
								<label>Graph by</label>

								<div class="right">
									<div class="right--half">
										<FormGranulationDropdownComponent
											@metricLimitations={{this.blockSetup.limitations}}
											@onChange={{this.changeGranulation}}
											@theme="dark"
										/>
									</div>

									{{#if this.visualization.showCumulative}}
										<div class="right--half">
											<FormCumulativeSwitchComponent
												@metricLimitations={{this.blockSetup.limitations}}
												@onChange={{this.changeCumulative}}
											/>
										</div>
									{{/if}}
								</div>
							</div>
						{{/if}}

					{{else}}

						<div class="bs__metrics">
							<a class="bs__metrics__add" {{on "click" this.addFirstDataSelect}}>
								<CommonUiIconComponent @name="Plus" @size="md" />Add first metric
							</a>
						</div>

						{{#if this.showQuickCreateFromBoard}}
							<BlockSettingsQuickCreateComponent @blockM={{@model}} />
						{{/if}}

					{{/if}}

				</DesignerPanelSectionComponent>

				{{#if this.hasAnyDataSelects}}

					<DesignerPanelSectionComponent
						@title="Metrics"
						@index={{2}}
						@isOpen={{this.isSectionMetricsOpen}}
						@toggle={{this.toggleSectionMetrics}}
					>

						<BlockSettingsMetricsComponent
							@blockM={{@model}}
							@isPanelOpen={{@isPanelOpen}}
							@onMetricOpen={{this.onMetricOpen}}
						/>

						{{#if this.showQuickCreateFromBoard}}
							<BlockSettingsQuickCreateComponent @blockM={{@model}} />
						{{/if}}

					</DesignerPanelSectionComponent>

					<DesignerPanelSectionComponent
						@title="Additional settings"
						@index={{3}}
						@isOpen={{this.isSectionOtherOpen}}
						@toggle={{this.toggleSectionOther}}
					>

						{{#if this.visualization.showTableColumnType}}
							<BlockSettingsTableColumnTypeComponent @blockM={{@model}} />
							{{#if this.visualization.showTableColumnInvert}}
								<BlockSettingsTableColumnInvertComponent @blockM={{@model}} />
							{{/if}}
						{{else if this.visualization.showSortDataSelect}}
							<BlockSettingsMetricSortDataSelectComponent
								@blockM={{@model}}
								@theme="dark"
							/>
						{{/if}}

						{{#if this.visualization.showBlockRecordLimitChoice}}
							<BlockSettingsRecordLimitComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showStackedChoiceOnBlock}}
							<BlockSettingsMetricStackedComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showOnlyMainMetricDimensions}}
							<BlockSettingsOnlyMainMetricDimensionsComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.allowOtherGrouping}}
							<BlockSettingsShowOtherComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showMediaSwitch}}
							<BlockSettingsMediaCreativesComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showSortOptions}}
							<BlockSettingsSortFieldComponent @blockM={{@model}} />
							{{#if this.showSortOrder}}
								<BlockSettingsSortOrderComponent @blockM={{@model}} />
							{{/if}}
						{{/if}}

						{{#if this.visualization.showLegendPosition}}
							<BlockSettingsLegendPositionComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showAggregationChoiceOnBlock}}
							<BlockSettingsMetricAggregationComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showNumberRowChoice}}
							<BlockSettingsNumberRowsComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showConversionSettings}}
							<BlockSettingsConversionComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showItemsInLineChoice}}
							<BlockSettingsItemsInLineComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showLabelsChoiceOnBlock}}
							<BlockSettingsMetricShownLabelsComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.canShowAndDisableNumberOnBlock}}
							<BlockSettingsShowNumberComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.showSmallChartInAdditionalSettings}}
							<BlockSettingsSmallChartComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showLabelHeader}}
							<BlockSettingsLabelHeaderComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showAiSummarySettings}}
							<BlockSettingsAiSummaryComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showTrafficLightsSettings}}
							<BlockSettingsTrafficLightsComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showHideTimeLeftSettings}}
							<BlockSettingsHideTimeLeftComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showFontSize}}
							<BlockSettingsFontSizeComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showYAxisSettings}}
							<BlockSettingsYAxisComponent @blockM={{@model}} @scaleIndex={{0}} />
						{{/if}}

						{{#if this.visualization.showSplineChoice}}
							<BlockSettingsSplineComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.showTotalsChoice}}
							<BlockSettingsShowTotalsComponent @blockM={{@model}} />
						{{/if}}

						{{#if this.visualization.showYAxisSettings}}
							<BlockSettingsYAxisComponent @blockM={{@model}} @scaleIndex={{1}} />
						{{/if}}

						{{yield to="additionalSettings"}}

					</DesignerPanelSectionComponent>

				{{/if}}

			{{/if}}
		</div>
	</template>
}
